/* Filtrous B2B contextual pricing — SINGLE PRICE AUTHORITY (v1.15, 2026-08-28).

   This file owns EVERY price the V3 app paints: the PDP buy price
   (.pdp-price), list/sheet rows (.fv3-price), search-results rows, and
   "Pairs with" / cross-sell cards (.pc-p). All of them render from the STATIC
   catalog-index.json, whose prices are baked at theme build time, so without
   this file a signed-in company contact sees LIST price everywhere even
   though checkout charges their contract price.

   v1.15 CONSOLIDATION (2026-08-28). Until now the PDP was owned by a separate
   pair — sections/filtrous-ctx-price.liquid + assets/filtrous-ctx-price.js —
   and this file carried a "MUST NOT TOUCH .pdp-price" rule because the two
   used to fight over the node. That pair is deleted. It cost a live incident:
   an update was pasted into the SECTION instead of the ASSET, so a section
   file that was no longer Liquid printed its entire contents as visible text
   on every product page, and window.FV3_CTX_PRICE stopped being emitted,
   silently killing contract pricing at the same time. Two files one keystroke
   apart in the editor's file list, doing one job. Now there is one file and
   one price source.

   PRICE SOURCE — one, for all three surfaces:
     templates/product.ctxprice.liquid via /products/<handle>?view=ctxprice,
     one fetch per product, cached per handle for the life of the page.
   Do NOT move this onto the Section Rendering API. It was tried
   (sections/filtrous-contextual-price.liquid, retired in v1.15): a company
   catalog only applies when customer.current_location is present, and that
   buyer-location context is not reliably attached to ?sections= requests, so
   Liquid silently fell back to LIST price and every listing kept showing list
   price for signed-in B2B contacts (2026-08-05). A ?view= request is a REAL
   page render, so the catalog resolves exactly as it does on the PDP itself.

   !! NEVER SEND Accept: application/json ON A ?sections= REQUEST !!
   On a /products/<handle> URL that header makes Shopify serve its PRODUCT
   JSON endpoint ({"product":{...}}) and IGNORE ?sections= entirely. The
   session check then found no filtrous-customer-state key, concluded "not
   logged in", and exited before touching anything — so "Pairs with" CARDS
   never got contract prices, while sheet ROWS worked fine because their page
   path is / where no product JSON endpoint exists. Same code, different URL,
   opposite result. Cost most of 2026-08-05 to find (David). Verified: with the
   header -> 3824 bytes of product JSON; without -> 342 bytes, section present.
   The remaining ?sections= call is the customer-state gate on NON-product
   pages; a PDP reads its session state off the ctxprice response instead and
   makes no customer-state call at all.

   DRIFT GUARD (2026-08-26 on the PDP, extended to rows and cards in v1.15).
   The struck-through "list" price is the baked catalog-index.json value, and
   it is only list price while the index is in step with Shopify. A 2026-08-14
   bake went stale against seven SKUs repriced upward after it, and the PDP
   showed the stale LOWER number struck through against the current one — a
   price rise dressed as a saving (MBP-10-2070 read $136.50 / $165.00).
   Rows and cards had the identical bug and never got the fix: v1.14's
   renderNegotiated() struck whenever listCents !== cents, in either direction.
   Now the strike requires listCents > cents on EVERY surface. A contract price
   is never ABOVE list, so a lower "list" is index drift, not a discount.
   The SWAP is deliberately left ungated: the Liquid value is the one checkout
   charges, so swapping it in is what keeps a drifted page honest. Disabling
   this file would put the stale index price back on the page.

   SHOW-WAS GATE. The strike additionally requires a signed-in company contact
   with a catalog context (b2b && catalog_context, both read off the ctxprice
   response). Comments have claimed this gate existed since 2026-08-26; it did
   not — the retired section passed a theme setting straight through. It is
   real as of v1.15.

   PDP IS UNGATED. The PDP is patched for signed-out visitors too, which is
   what corrects a drifted list price for the public. It costs one fetch, and
   it REPLACES the anonymous customer-state fetch that v1.14 made on every
   page, so the request count is unchanged. Rows and cards stay behind the
   signed-in gate: each costs its own fetch and an anonymous shopper on a
   48-row sheet would pay for 48 of them. So a logged-out visitor can still see
   a drifted price in a SHEET ROW or a CARD. Fixing that needs a bulk
   Liquid-rendered price endpoint, or rebaking the index on every price change.

   CFQ. Call-for-Quote SKUs (any SKU containing "OSK", plus EXK-BD-0096-PP)
   are never priced anywhere on the storefront, for any customer. isCFQ() is
   the same predicate as filtrous-v3.js and the hero-nine search patch. On the
   PDP filtrous-v3.js paints no .pdp-price node at all for them, so there is
   nothing to patch; the guard stays as belt and braces.

   LAYOUT — the three surfaces need DIFFERENT treatments, do not merge them:
     Table rows: the cell stays a table-cell (flexing the <td> collapses the
     Price column 143px -> 52px) and the struck list price is absolutely
     positioned OUT OF FLOW to the left, so the contract price itself stays
     centred on the column line.
     Cards: inline-flex pair, left-aligned like the rest of the card. The
     absolute treatment pushed the struck price outside the ~180px card and it
     was clipped (David 2026-08-05).
     PDP: a plain inline <s> before the price text, and the "/ pack" <small>
     suffix is REUSED VERBATIM rather than rebuilt.

   PDP RE-ENTRY. The app is a SPA-ish renderer and repaints .pdp-price in
   place. List price is read off the DOM ONCE per node and cached on the node
   (data-list-price). Re-deriving it on re-entry would be wrong: after a swap
   the visible number is the contract price, so a second pass would strike the
   contract price and promote list — inverting the two. The painted SKU in
   .fv3-buy .part must also still match FV3_CONFIG.pdpSku, or the app has
   routed to a different product than the one this fetch was for.

   DEBUG: append ?b2bdebug=1 for an on-screen panel (gate result, index size,
   cards found, per-sku why-not). Inert without the flag. STRIP BEFORE
   PUBLISHING to customers.

   Marker discipline: the "already patched" marker lives on the PRICE node, and
   alreadyPatched() also requires .b2b-pair to still be present — the app can
   re-render a card's contents in place, which would strand a container-level
   marker (the v1.5-v1.9 card bug).

   After ANY edit the CDN serves the previous MINIFIED build until the page
   HTML picks up the new ?v= — retest with a FRESH unique query param.

   Revert: restore this file to v1.14, restore sections/filtrous-ctx-price
   .liquid + assets/filtrous-ctx-price.js from theme version history, and add
   the ctx_price section back to templates/product.json. */
(function () {
  'use strict';

  var CFG = window.FV3_CONFIG || {};
  var INDEX_URL = CFG.indexUrl || '/catalog-index.json';
  var CTX_VIEW = 'ctxprice';
  var STATE_SECTION = 'filtrous-customer-state';
  var WAS_COLOR = '#5c6f7e';
  var DEBUG = /[?&]b2bdebug=1/.test(location.search);

  /* Emitted by sections/filtrous-v3-app.liquid on product templates only, so
     it doubles as "this page is a PDP". */
  var PDP_SKU = CFG.pdpSku ? String(CFG.pdpSku).toUpperCase() : null;
  var pdpCtx = null;

  var bySku = null;
  var ctxCache = {};
  var scheduled = false;
  var rowObserver = null;
  var listReady = false;

  var diag = [];
  var painted = false;
  function D(k, v) {
    if (!DEBUG) return;
    diag.push([k, String(v)]);
    if (painted) paint(); else setTimeout(paint, 400);
  }
  function paint() {
    if (!DEBUG) return;
    var box = document.getElementById('b2b-diag-panel');
    if (!box) {
      box = document.createElement('div');
      box.id = 'b2b-diag-panel';
      box.setAttribute('style', 'position:fixed;z-index:2147483647;top:8px;right:8px;width:430px;max-height:94vh;overflow:auto;background:#0b2740;color:#e8f6f2;font:12px/1.45 ui-monospace,Menlo,monospace;padding:12px 14px;border-radius:10px;box-shadow:0 10px 40px rgba(0,0,0,.45);');
      document.body.appendChild(box);
      painted = true;
    }
    var html = '<div style="font-weight:700;color:#5fe3c0;margin-bottom:7px">B2B PRICING DIAGNOSTIC</div>';
    for (var i = 0; i < diag.length; i++) {
      html += '<div style="margin:2px 0;word-break:break-word"><span style="color:#9db1bf">' +
        diag[i][0] + ':</span> ' + diag[i][1].replace(/[<>]/g, '') + '</div>';
    }
    box.innerHTML = html;
  }

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* money string -> integer cents, for comparing a DOM-read value to Liquid */
  function toCents(v) {
    var n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
    return isFinite(n) ? Math.round(n * 100) : null;
  }

  /* Call for Quote — same rule as filtrous-v3.js and the hero-nine search
     patch. These SKUs are never priced anywhere, for any customer. */
  function isCFQ(sku) {
    var s = String(sku || '').toUpperCase();
    return s.indexOf('OSK') >= 0 || s === 'EXK-BD-0096-PP';
  }

  /* The strike is a claim that the customer is saving money. Only a signed-in
     company contact WITH a catalog context can be. */
  function showWas(meta) {
    return !!(meta && meta.b2b && meta.catalog_context);
  }

  /* A struck value is only a saving when it is strictly ABOVE the price being
     shown — see the DRIFT GUARD note in the header. */
  function isSaving(listCents, cents) {
    return listCents != null && cents != null && listCents > cents;
  }

  function renderNegotiated(priceEl, neg) {
    priceEl.textContent = '';
    priceEl.classList.add('b2b-patched');
    var pair = document.createElement('span');
    pair.className = 'b2b-pair';
    if (showWas(neg.meta) && isSaving(neg.listCents, neg.cents)) {
      var was = document.createElement('s');
      was.className = 'b2b-was';
      was.textContent = money(neg.listCents);
      pair.appendChild(was);
    }
    var now = document.createElement('span');
    now.className = 'b2b-now';
    now.textContent = money(neg.cents);
    pair.appendChild(now);
    priceEl.appendChild(pair);
  }

  function alreadyPatched(priceEl, sku) {
    return priceEl.getAttribute('data-b2b-sku') === sku && !!priceEl.querySelector('.b2b-pair');
  }

  function sectionJSON(html, id) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var node = doc.getElementById(id);
      return node ? JSON.parse(node.textContent) : null;
    } catch (e) { return null; }
  }

  // NOTE: deliberately NO Accept header — see the warning at the top of file.
  function fetchSection(path, section) {
    return fetch(path + '?sections=' + section, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return j && j[section] ? j[section] : null; })
      .catch(function () { return null; });
  }

  function loadIndex() {
    return fetch(INDEX_URL)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        bySku = {};
        (data.products || []).forEach(function (p) {
          if (!p.sku) return;
          var cents = Math.round(parseFloat(p.price) * 100);
          bySku[String(p.sku).toUpperCase()] = { handle: p.handle, listCents: isNaN(cents) ? null : cents };
        });
        D('index skus', Object.keys(bySku).length);
      })
      .catch(function (e) { bySku = {}; D('index LOAD FAILED', e && e.message); });
  }

  /* Resolves to { meta: <full ctxprice payload>, cents: {SKU: int} } or null.
     Unlike v1.14 this does NOT drop the response for signed-out sessions — the
     PDP needs it to correct index drift for the public. Callers that must not
     act on a signed-out session check meta.logged_in themselves. */
  function ctxFor(handle) {
    if (!ctxCache[handle]) {
      ctxCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '?view=' + CTX_VIEW, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) D('ctx HTTP ' + handle, r.status); return r.ok ? r.text() : null; })
        .then(function (txt) {
          if (!txt) return null;
          var d;
          try { d = JSON.parse(txt); }
          catch (e) { D('ctx NOT JSON ' + handle, txt.length + ' chars: ' + txt.slice(0, 50)); return null; }
          D('ctx ' + handle, 'logged_in=' + d.logged_in + ' catalog=' + d.catalog_context);
          if (!d || !d.variants) return null;
          var map = {};
          d.variants.forEach(function (v) {
            if (v.sku != null && typeof v.cents === 'number') map[String(v.sku).toUpperCase()] = v.cents;
          });
          return { meta: d, cents: map };
        })
        .catch(function (e) { D('ctx FETCH FAILED ' + handle, e && e.message); return null; });
    }
    return ctxCache[handle];
  }

  function negotiatedFor(sku, why) {
    /* Quote-only SKUs are never priced — exit before any ctx fetch so the
       app's "Call for Quote" cell is left exactly as it was painted. */
    if (isCFQ(sku)) { if (why) D(why + ' ' + sku, 'CALL FOR QUOTE - never patched'); return Promise.resolve(null); }
    var entry = bySku && bySku[sku];
    if (!entry || !entry.handle) { if (why) D(why + ' ' + sku, 'NOT IN INDEX'); return Promise.resolve(null); }
    return ctxFor(entry.handle).then(function (res) {
      if (!res) { if (why) D(why + ' ' + sku, 'no ctx data'); return null; }
      if (!res.meta.logged_in) { if (why) D(why + ' ' + sku, 'signed out'); return null; }
      if (!(sku in res.cents)) { if (why) D(why + ' ' + sku, 'sku absent from ctx response'); return null; }
      var cents = res.cents[sku];
      if (entry.listCents != null && cents === entry.listCents) {
        if (why) D(why + ' ' + sku, 'ctx == list (' + cents + ')');
        return null;
      }
      /* Index drift: the baked "list" is BELOW the live price. Still swap —
         the Liquid value is what checkout charges — but renderNegotiated will
         not strike it. See the DRIFT GUARD note in the header. */
      if (why) {
        D(why + ' ' + sku, (isSaving(entry.listCents, cents) ? 'SWAP ' : 'SWAP (drift, no strike) ') +
          entry.listCents + ' -> ' + cents);
      }
      return { cents: cents, listCents: entry.listCents, meta: res.meta };
    });
  }

  /* ---------------- PDP ---------------- */

  function pdpHandle() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  /* text of the price node with the unit suffix and any prior strike removed */
  function bareText(el) {
    var probe = el.cloneNode(true);
    var ps = probe.querySelector('small');
    if (ps && ps.parentNode) ps.parentNode.removeChild(ps);
    var pw = probe.querySelector('s');
    if (pw && pw.parentNode) pw.parentNode.removeChild(pw);
    return (probe.textContent || '').trim();
  }

  function patchPdp() {
    if (!pdpCtx || !PDP_SKU || isCFQ(PDP_SKU)) return;
    var cents = pdpCtx.cents[PDP_SKU];
    if (typeof cents !== 'number') return;

    var app = document.getElementById('fv3-app');
    if (!app) return;

    /* the app is a SPA-ish renderer — only patch while the painted product is
       the one this response was fetched for */
    var part = app.querySelector('.fv3-buy .part');
    if (!part) return;
    if (part.textContent.trim().toUpperCase() !== PDP_SKU) return;

    var el = app.querySelector('.fv3-buy .pdp-price');
    if (!el) return;

    var ctxStr = money(cents);
    if (el.getAttribute('data-ctx-price') === ctxStr) return;

    /* capture list price once per node — see PDP RE-ENTRY in the header */
    var listStr = el.getAttribute('data-list-price');
    if (listStr === null) {
      listStr = bareText(el);
      el.setAttribute('data-list-price', listStr);
    }

    /* the unit suffix ("/ pack") is reused verbatim */
    var small = el.querySelector('small');

    el.textContent = '';

    var listCents = toCents(listStr);
    if (showWas(pdpCtx.meta) && isSaving(listCents, cents)) {
      var was = document.createElement('s');
      was.className = 'b2b-was pdp-was';
      was.textContent = listStr;
      el.appendChild(was);
    }

    el.appendChild(document.createTextNode(ctxStr + (small ? ' ' : '')));
    if (small) el.appendChild(small);
    el.setAttribute('data-ctx-price', ctxStr);
    D('pdp ' + PDP_SKU, listCents + ' -> ' + cents +
      (showWas(pdpCtx.meta) && isSaving(listCents, cents) ? '' : ' (no strike)'));
  }

  /* ---------------- list views ---------------- */

  function patchCards() {
    var cards = document.querySelectorAll('#fv3-app .fv3-pcard');
    if (DEBUG) D('cards found', cards.length);
    for (var i = 0; i < cards.length; i++) {
      (function (card) {
        var skuEl = card.querySelector('.pc-sku b');
        var priceEl = card.querySelector('.pc-p');
        if (!skuEl || !priceEl) return;
        var sku = skuEl.textContent.trim().toUpperCase();
        if (!sku || isCFQ(sku) || alreadyPatched(priceEl, sku)) return;
        priceEl.setAttribute('data-b2b-sku', sku);
        negotiatedFor(sku, 'card').then(function (neg) {
          if (!neg || !priceEl.isConnected) return;
          renderNegotiated(priceEl, neg);
          D('card ' + sku, 'PATCHED');
        });
      })(cards[i]);
    }
  }

  function rowSku(row) {
    var s = row.getAttribute('data-sku');
    if (!s) {
      var part = row.querySelector('.fv3-part');
      s = part ? part.textContent : '';
    }
    return (s || '').trim().toUpperCase();
  }

  function patchRow(row) {
    var sku = rowSku(row);
    var priceEl = row.querySelector('.fv3-price');
    if (!sku || !priceEl) return;
    if (isCFQ(sku)) return;
    if (alreadyPatched(priceEl, sku)) return;
    priceEl.setAttribute('data-b2b-sku', sku);
    negotiatedFor(sku).then(function (neg) {
      if (!neg || !priceEl.isConnected) return;
      renderNegotiated(priceEl, neg);
    });
  }

  function patchRows() {
    var candidates = document.querySelectorAll('#fv3-app tr');
    var rows = [];
    for (var c = 0; c < candidates.length; c++) {
      if (candidates[c].querySelector('.fv3-price') && rowSku(candidates[c])) rows.push(candidates[c]);
    }
    if (!rows.length) return;
    if ('IntersectionObserver' in window) {
      if (!rowObserver) {
        rowObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            rowObserver.unobserve(entry.target);
            patchRow(entry.target);
          });
        }, { rootMargin: '300px 0px' });
      }
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].getAttribute('data-b2b-seen')) continue;
        rows[i].setAttribute('data-b2b-seen', '1');
        rowObserver.observe(rows[i]);
      }
    } else {
      for (var j = 0; j < rows.length; j++) patchRow(rows[j]);
    }
  }

  function patch() {
    scheduled = false;
    patchPdp();
    if (listReady) { patchCards(); patchRows(); }
  }

  function schedule() {
    /* The PDP swap is ONE node and must not wait on requestAnimationFrame: a
       backgrounded tab throttles rAF, and the retired ctx-price script patched
       synchronously straight from its observer. Only the list passes, which
       walk every row and card, are worth batching to a frame. */
    patchPdp();
    if (!listReady || scheduled) return;
    scheduled = true;
    requestAnimationFrame(patch);
  }

  function startListViews() {
    if (listReady) return;
    loadIndex().then(function () {
      listReady = true;
      patch();
    });
  }

  function injectCSS() {
    var css = document.createElement('style');
    css.textContent =
      // shared type for all three surfaces
      '#fv3-app .b2b-was{font-weight:500;font-size:.9em;color:' + WAS_COLOR + ';text-decoration-color:' + WAS_COLOR + ';white-space:nowrap;}' +
      '#fv3-app .b2b-now{white-space:nowrap;}' +
      // TABLE ROWS: contract price centred on the column line, struck price out of flow.
      '#fv3-app td.fv3-price.b2b-patched{text-align:center;}' +
      '#fv3-app td.fv3-price .b2b-pair{position:relative;display:inline-block;}' +
      '#fv3-app td.fv3-price .b2b-was{position:absolute;right:100%;top:0;margin-right:8px;}' +
      // CARDS: inline, left-aligned with the rest of the card. Absolute here
      // pushed the struck price outside the card and it was clipped.
      '#fv3-app .pc-p .b2b-pair{display:inline-flex;align-items:baseline;gap:8px;max-width:100%;flex-wrap:wrap;}' +
      // PDP: plain inline strike ahead of the price, "/ pack" suffix untouched.
      '#fv3-app .fv3-buy .pdp-price .pdp-was{margin-right:8px;font-size:15px;}';
    document.head.appendChild(css);
  }

  function boot() {
    var mount = document.getElementById('fv3-app');
    D('page', location.pathname);
    D('#fv3-app', !!mount);
    if (!mount) return;

    injectCSS();
    new MutationObserver(schedule).observe(mount, { childList: true, subtree: true });

    /* PRODUCT PAGE: one ctxprice fetch serves both the PDP swap (ungated) and
       the signed-in gate for its "Pairs with" cards — no customer-state call. */
    if (PDP_SKU) {
      var handle = pdpHandle();
      D('pdp sku', PDP_SKU + ' handle=' + handle);
      if (!handle) return;
      ctxFor(handle).then(function (res) {
        if (!res) { D('RESULT', 'no ctx payload - nothing patched'); return; }
        pdpCtx = res;
        patchPdp();
        D('gate logged_in', res.meta.logged_in + ' (from ctxprice)');
        if (!res.meta.logged_in) { D('RESULT', 'GATE CLOSED - rows/cards untouched'); return; }
        startListViews();
      });
      return;
    }

    /* EVERY OTHER TEMPLATE: no product in scope, so the session gate needs its
       own tiny fetch before we start spending one request per visible SKU. */
    fetchSection(location.pathname, STATE_SECTION).then(function (html) {
      var state = html && sectionJSON(html, 'fn-customer-state');
      D('gate logged_in', state ? state.logged_in : 'no state');
      D('gate company', state ? state.company : '-');
      if (!state || !state.logged_in) { D('RESULT', 'GATE CLOSED - rows/cards untouched'); return; }
      startListViews();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
